/**
 * FactoringEntitiesTab — TES-01: CRUD de Entidades de Factoring
 */
import { useState } from 'react'
import { Plus, Loader2, Building2, CheckCircle2, XCircle } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import { useFactoringEntities, useCreateFactoringEntity, useUpdateFactoringEntity, type FactoringEntity } from '../hooks/useTreasury'
import { formatRut } from '@/lib/utils'
import { EmptyState } from '@/components/EmptyState'
import { Modal } from '@/components/ds/Modal'

// ─── Modal ─────────────────────────────────────────────────────────────────────

function FactoringEntityModal({
  entity,
  onClose,
}: {
  entity: FactoringEntity | null
  onClose: () => void
}) {
  const create = useCreateFactoringEntity()
  const update = useUpdateFactoringEntity()

  const [form, setForm] = useState({
    legal_name: entity?.legal_name ?? '',
    tax_id: entity?.tax_id ?? '',
    contact_email: entity?.contact_email ?? '',
    phone: entity?.phone ?? '',
    address: entity?.address ?? '',
    notes: entity?.notes ?? '',
    is_active: entity?.is_active ?? true,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const set = (field: string, value: string | boolean) =>
    setForm(f => ({ ...f, [field]: value }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    try {
      if (entity) {
        await update.mutateAsync({ id: entity.id, ...form })
      } else {
        await create.mutateAsync(form)
      }
      onClose()
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: { details?: Record<string, string[]> } } } }
      const details = e.response?.data?.error?.details
      if (details) {
        const mapped: Record<string, string> = {}
        Object.entries(details).forEach(([k, v]) => { mapped[k] = Array.isArray(v) ? v[0] : String(v) })
        setErrors(mapped)
      }
    }
  }

  const isPending = create.isPending || update.isPending

  return (
    <Modal open onClose={onClose} title={entity ? 'Editar Entidad de Factoring' : 'Nueva Entidad de Factoring'} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label htmlFor="factoringentitiestab-legal-name" className="block text-xs font-medium text-content-muted mb-1">Nombre Legal *</label>
            <input id="factoringentitiestab-legal-name"
              value={form.legal_name}
              onChange={e => set('legal_name', e.target.value)}
              required
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
            />
            {errors.legal_name && <p className="text-xs text-danger-solid mt-1">{errors.legal_name}</p>}
          </div>
          <div>
            <label htmlFor="factoringentitiestab-tax-id" className="block text-xs font-medium text-content-muted mb-1">RUT / Tax ID *</label>
            <input id="factoringentitiestab-tax-id"
              value={form.tax_id}
              onChange={e => set('tax_id', e.target.value)}
              required
              placeholder="12.345.678-9"
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
            />
            {errors.tax_id && <p className="text-xs text-danger-solid mt-1">{errors.tax_id}</p>}
          </div>
          <div>
            <label htmlFor="factoringentitiestab-phone" className="block text-xs font-medium text-content-muted mb-1">Teléfono</label>
            <input id="factoringentitiestab-phone"
              value={form.phone}
              onChange={e => set('phone', e.target.value)}
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="col-span-2">
            <label htmlFor="factoringentitiestab-contact-email" className="block text-xs font-medium text-content-muted mb-1">Email de contacto</label>
            <input id="factoringentitiestab-contact-email"
              type="email"
              value={form.contact_email}
              onChange={e => set('contact_email', e.target.value)}
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="col-span-2">
            <label htmlFor="factoringentitiestab-address" className="block text-xs font-medium text-content-muted mb-1">Dirección</label>
            <input id="factoringentitiestab-address"
              value={form.address}
              onChange={e => set('address', e.target.value)}
              placeholder="Ej: Huerfanos 740 piso 5 Santiago"
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
            />
            <p className="text-xs text-content-muted mt-1">
              Obligatoria para ceder facturas al SII: va en el AEC como dirección del cesionario.
            </p>
          </div>
          <div className="col-span-2">
            <label htmlFor="factoringentitiestab-notes" className="block text-xs font-medium text-content-muted mb-1">Notas</label>
            <textarea id="factoringentitiestab-notes"
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              rows={2}
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary resize-none"
            />
          </div>
          {entity && (
            <div className="col-span-2 flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={form.is_active}
                onChange={e => set('is_active', e.target.checked)}
                className="rounded"
              />
              <label htmlFor="is_active" className="text-sm text-content-muted">Entidad activa</label>
            </div>
          )}
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose}
            className="flex-1 px-4 py-2 border border-divider rounded-lg text-sm text-content-muted hover:bg-soft">
            Cancelar
          </button>
          <button type="submit" disabled={isPending}
            className="flex-1 px-4 py-2 bg-primary text-on-primary rounded-lg text-sm hover:bg-primary-hover disabled:opacity-50 flex items-center justify-center gap-2">
            {isPending && <Loader2 size={13} className="animate-spin" />}
            {entity ? 'Guardar' : 'Crear'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ─── Tab ───────────────────────────────────────────────────────────────────────

export function FactoringEntitiesTab() {
  const [showModal, setShowModal] = useState(false)
  const [editEntity, setEditEntity] = useState<FactoringEntity | null>(null)

  const { data, isLoading } = useFactoringEntities()
  const entities = data?.entities ?? []

  const handleEdit = (entity: FactoringEntity) => {
    setEditEntity(entity)
    setShowModal(true)
  }

  const handleClose = () => {
    setShowModal(false)
    setEditEntity(null)
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-content-muted">
          {data ? `${data.count} entidad${data.count !== 1 ? 'es' : ''}` : ''}
        </p>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover"
        >
          <Plus size={14} />
          Nueva Entidad
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-content-subtle">
          <Loader2 size={20} className="animate-spin mr-2" />Cargando...
        </div>
      ) : entities.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No hay entidades de factoring registradas."
          size="md"
        />
      ) : (
        <DataTable
          columns={[
            { key: 'legal_name', header: 'Razón Social', render: e => <span className="font-medium text-content">{e.legal_name}</span> },
            { key: 'tax_id', header: 'RUT', render: e => <span className="font-mono">{formatRut(e.tax_id)}</span> },
            { key: 'contact_email', header: 'Email', render: e => e.contact_email || '—' },
            { key: 'phone', header: 'Teléfono', render: e => e.phone || '—' },
            {
              key: 'is_active', header: 'Estado',
              render: e => e.is_active
                ? <CheckCircle2 size={15} className="text-success-solid" />
                : <XCircle size={15} className="text-content-subtle" />,
            },
            {
              key: 'acciones', header: '',
              render: e => (
                <div className="text-right">
                  <button onClick={() => handleEdit(e)} className="text-xs text-primary hover:text-primary-hover font-medium">
                    Editar
                  </button>
                </div>
              ),
            },
          ]}
          rows={entities}
          rowKey={e => e.id}
          minWidth={820}
          caption="Entidades de factoring"
        />
      )}

      {showModal && (
        <FactoringEntityModal
          entity={editEntity}
          onClose={handleClose}
        />
      )}
    </div>
  )
}
